import React from 'react';
// import { Helmet } from "react-helmet-async";

// ここに履歴を追加していくだけでOK（新しい順で先頭に追加）
const CHANGELOG = [
  {
    version: 'v1.4.0',
    date: '2025/09/30',
    items: [
      '「画像から反映（OCR）」を追加：出玉の数字一覧画像から合計を自動計算し、フォームへ即時反映',
      '詳細入力ページの「獲得出玉（合計）」の下にボタンを配置／出玉計算モーダルの処理を共通化',
      'ツールチップにサンプル画像（数字一覧のみ取り込み可能）を表示',
    ],
  },
  {
    version: 'v1.3.0',
    date: '2025/09/23',
    items: [
      '任意入力の「店舗名」「機種名」をフォームに追加（初期は折りたたみ／非表示）',
      '履歴ページへの表示・復元・CSV列を対応（storeName, machineName）',
      'XSS対策と制御文字サニタイズを強化',
    ],
  },
  {
    version: 'v1.2.0',
    date: '2025/09/16',
    items: [
      'PWA対応を強化（オフライン対応の安定化・キャッシュ戦略の見直し・SWのバージョン管理）',
      'InstallPrompt を実装（ホーム画面追加の促進）',
      'ヘルプに PWA の使い方・注意点・FAQ を追記',
    ],
  },
  {
    version: 'v1.1.0',
    date: '2025/09/03',
    items: ['履歴機能をリリース（保存・一覧・復元・削除・検索・ソート・CSVエクスポート）'],
  },
  {
    version: 'v1.0.0',
    date: '2025/08/26',
    items: ['初回リリース（簡易・詳細フォーム、出玉計算モーダル、回転率計算）'],
  },
];

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-blue-800 mb-2">{title}</h2>
    <div className="text-sm leading-relaxed text-gray-700 space-y-2">{children}</div>
  </section>
);

export default function Help() {
  const latest = CHANGELOG[0];

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* <Helmet>
        <title>PachiCalc ヘルプ・使い方ガイド</title>
        <meta
          name="description"
          content="PachiCalcの使い方、履歴（保存・復元）機能、出玉モーダル、PWA（インストール・オフライン）やFAQ、更新履歴をまとめたヘルプページです。"
        />
      </Helmet> */}

      <h1 className="text-2xl font-bold text-blue-900 mb-4">PachiCalc ヘルプ・使い方ガイド</h1>

      <Section title="PachiCalcとは？">
        <p>
          パチンコの回転率（1Kあたりの回転数）を簡単に計算できるアプリです。
          通常時回転数・出玉・差玉などを入力するだけで、回転率を自動算出します。
        </p>
        <p>
          本アプリは<strong>PWA（Progressive Web App）</strong>
          として提供しており、インストール（ホーム画面追加）しておくと、
          オフライン環境でも主要機能を利用できます（詳細は「PWA：インストールとオフライン」参照）。
        </p>
        <p className="text-xs text-gray-500">
          ※
          本アプリは端末のローカル環境で動作し、入力データや履歴は端末（ブラウザ）内に保存されます。
        </p>
      </Section>

      <Section title="機能：履歴（保存・一覧・復元）">
        <p>
          計算結果を保存しておき、あとから<strong>履歴ページ</strong>
          で一覧確認・復元できます。 フォームは<strong>簡易／詳細</strong>
          の両モードに対応しています。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>保存：</b>計算後に「保存する」ボタンで履歴に追加
          </li>
          <li>
            <b>一覧：</b>画面下メニューの「履歴」からカード表示
          </li>
          <li>
            <b>復元：</b>カードの「復元」でフォームに反映
          </li>
          <li>
            <b>上限：</b>最大 <b>50件</b>（古いものから自動削除）
          </li>
          <li>
            <b>便利機能：</b>検索／並び替え／CSVエクスポート／一括削除
          </li>
        </ul>
      </Section>

      {/* 追加：任意の店舗名・機種名 */}
      <Section title="任意入力：店舗名・機種名">
        <p>
          入力フォームに<strong>任意項目</strong>として「店舗名」「機種名」を追加しました。
          初期表示は<strong>折りたたみ（非表示）</strong>
          になっており、必要な場合のみ展開して入力できます。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>保存・復元：</b>
            他の入力値と同様に保存され、履歴からの復元でもフォームに反映されます。
          </li>
          <li>
            <b>履歴表示：</b>履歴カードに「店舗名」「機種名」を表示（未入力時は「-」）。
          </li>
          <li>
            <b>CSV：</b>エクスポート時に <code>storeName</code> / <code>machineName</code>{' '}
            列が追加されます。
          </li>
          <li>
            <b>安全性：</b>
            テキストはプレーン表示し、制御文字のサニタイズや式インジェクション対策を行っています。
          </li>
        </ul>
      </Section>

      {/* 追加：画像から反映（OCR） */}
      <Section title="機能：画像から反映（OCR）">
        <p>
          出玉の<strong>数字一覧</strong>を撮影した画像から、<strong>獲得出玉（合計）</strong>
          を自動計算して
          フォームに反映できます。精度が高い場合はワンクリックで合計が入力され、低信頼の場合は注意が表示されます。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>使い方：</b> 詳細入力ページの「獲得出玉（合計）」の下、
            「出玉計算用」ボタンの左にある<strong>「画像から反映」</strong>
            を押して、数字一覧のスクショを選択。
          </li>
          <li>
            <b>対応フォーマット：</b> <code>1200</code> / <code>1,200</code> / <code>500x3</code> /{' '}
            <code>500*3</code> などの数値行に対応。 認識結果は内部で合計されます。
          </li>
          <li>
            <b>撮影のコツ：</b> 数字部分のみを大きく、明るく、ピントが合うように撮影してください。
            罫線や余白は少ないほど安定します。
          </li>
          <li>
            <b>ツールチップ：</b> ボタン右の ⓘ にマウスオーバーでサンプル画像を表示します（
            <code>img/ocr_sample.png</code>）。
          </li>
          <li>
            <b>プライバシー：</b> 画像はブラウザ内で解析され、サーバーに送信されません（PWA対応）。
          </li>
        </ul>
        <p className="text-xs text-gray-500">
          ※ 画像の品質や端末・ブラウザの仕様により、誤認識や未認識が発生する場合があります。
          低信頼と判定された場合は内容をご確認のうえ、必要に応じて修正してください。
        </p>
      </Section>

      <Section title="PWA：インストールとオフライン">
        <p>
          PachiCalc は PWA に対応しています。以下の手順でホーム画面へ追加・インストールできます。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>Android（Chrome）:</b>{' '}
            ページ下部のバナーやブラウザのメニューから「ホーム画面に追加」。追加後はランチャー（アプリ一覧）からスタンドアロンで起動できます。
          </li>
          <li>
            <b>PC（Chrome/Edge）:</b>{' '}
            アドレスバーのインストールアイコンやメニューから「インストール」。デスクトップ/アプリランチャーに追加され、オフラインでも起動可能です。
          </li>
          <li>
            <b>iOS（Safari）:</b> 共有メニューの「ホーム画面に追加」を利用してください。挙動は OS /
            Safari のバージョンに依存し、一部の環境では挙動が変化する場合があります。
          </li>
        </ul>
        <p>
          インストール後はトップページやヘルプ・免責事項などの主要ページが
          <strong>オフラインでも閲覧</strong>できます。
          ただし、初回アクセス時に必要ファイルのキャッシュ取得が完了していることが前提です。
        </p>
        <p className="text-xs text-gray-500">
          ※ 端末やブラウザの仕様変更により、PWA の挙動が予告なく変わる場合があります。
        </p>
      </Section>

      <Section title="オフラインとキャッシュの挙動">
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>キャッシュ対象：</b>{' '}
            アプリの静的アセット（HTML/CSS/JS/アイコン）と主要ページ（トップ/ヘルプ/免責事項）。
          </li>
          <li>
            <b>バージョン管理：</b> Service Worker の <code>CACHE_NAME</code>{' '}
            を更新すると、新バージョン配信時に古いキャッシュがクリーンアップされます。
          </li>
          <li>
            <b>初回取得：</b>{' '}
            初回アクセス時に必要ファイルをキャッシュします。通信が不安定な環境では完了まで時間がかかる場合があります。
          </li>
          <li>
            <b>入力データ：</b> 履歴は <code>localStorage</code>{' '}
            に保存され、ネットワークに送信されません。
          </li>
        </ul>
      </Section>

      <Section title="更新の受け取り方（UpdateBanner）">
        <p>
          新しいバージョンが配信されると、画面上部に<strong>更新バナー</strong>が表示されます。
          「更新」ボタンで最新キャッシュを取得し、アプリを再読み込みすると新バージョンが適用されます。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>更新が表示されない場合は、数分待ってから再読み込みしてください。</li>
          <li>強制更新したい場合は、ブラウザのキャッシュ削除やハードリロードをお試しください。</li>
        </ul>
      </Section>

      <Section title="GA4での計測（準備中）">
        <p>
          今後、ホーム画面追加（A2HS）やオフライン利用の計測イベントを順次追加予定です。公開後に本ヘルプへ計測仕様を追記します。
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            例：<code>analytics_event:add_to_home_screen</code>、
            <code>analytics_event:offline_mode</code> など
          </li>
        </ul>
      </Section>

      <Section title="入力項目の説明">
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>通常時回転数</b>：通常時に回した回転数
          </li>
          <li>
            <b>チャンス中回転数</b>：確変・時短中の回転数
          </li>
          <li>
            <b>獲得出玉（合計）</b>：大当たりで得た出玉の合計（モーダルで内訳複数入力可／
            <b>画像から反映に対応</b>）
          </li>
          <li>
            <b>最終差玉</b>：グラフの最終位置の差玉
          </li>
          <li>
            <b>持ち玉減少率</b>：電サポ中の減少割合（例：0.1 = 10%）
          </li>
          <li>
            <b>1000円あたりの貸し玉数</b>：店舗の貸玉数（一般的に250）
          </li>
          <li>
            <b>店舗名（任意）</b>：記録用のテキスト（履歴/CSVに保存）
          </li>
          <li>
            <b>機種名（任意）</b>：記録用のテキスト（履歴/CSVに保存）
          </li>
        </ul>
        <p className="text-xs text-gray-500">
          ツールチップ（ⓘ）のサンプル画像に赤枠を表示しているので、参照しながら入力できます。
        </p>
      </Section>

      <Section title="計算について">
        <p>入力された数値をもとに、アプリが自動的に回転率を算出します。</p>
        <p className="text-xs text-gray-500">
          ※ 計算結果はあくまで目安です。実際のホール状況により差異が生じる場合があります。
        </p>
      </Section>

      <Section title="出玉計算モーダルの使い方">
        <ol className="list-decimal pl-6 space-y-1">
          <li>「出玉計算用」ボタンを押してモーダルを開く</li>
          <li>
            1行につき1項目を入力（例：<code>1200</code> / <code>1,200</code> / <code>500x3</code>）
          </li>
          <li>
            <code>{'//'}</code> や <code>#</code> 以降はコメント
          </li>
          <li>合計が自動計算され、「獲得出玉（合計）」に反映</li>
          <li>
            <b>画像から反映：</b> モーダル内の「画像取り込み」でも数字一覧から合計を取得できます。
          </li>
        </ol>
      </Section>

      <Section title="バリデーションとエラー">
        <ul className="list-disc pl-6 space-y-1">
          <li>形式・範囲チェック（整数/数値、0〜1、正の数など）</li>
          <li>クロスチェックで「実投資が0以下」の場合は計算不能</li>
          <li>詳細ではモーダル合計と手入力合計の乖離を警告</li>
        </ul>
      </Section>

      <Section title="履歴の保存について（技術メモ）">
        <ul className="list-disc pl-6 space-y-1">
          <li>
            保存先は <b>localStorage</b>。端末/ブラウザごとに独立
          </li>
          <li>
            シークレット（プライベート）モードや一部アプリ内ブラウザでは保存不可/不安定な場合あり
          </li>
          <li>容量超過で保存失敗になることがあります。不要な履歴を削除してください</li>
          <li>
            本番利用は <b>HTTPS</b> を推奨。同一オリジン（ポート含む）ごとに履歴は別管理です
          </li>
          <li>
            保存レコードに <code>storeName</code> / <code>machineName</code>{' '}
            を追加。CSVにも同名列を出力します。
          </li>
          <li>
            入力テキストは制御文字の削除・式インジェクション対策などのサニタイズを実施しています。
          </li>
        </ul>
      </Section>

      {/* ===== アプリ情報 & 更新履歴 ===== */}
      <Section title="アプリ情報">
        <ul className="list-disc pl-6 space-y-1">
          <li>アプリ名：PachiCalc</li>
          <li>
            現在のバージョン：{latest.version}（{latest.date}）
          </li>
        </ul>
      </Section>

      <Section title="更新履歴（Changelog）">
        <div className="space-y-4">
          {CHANGELOG.map((entry) => (
            <div key={entry.version} className="border rounded-xl p-3">
              <div className="flex items-baseline justify-between">
                <div className="font-semibold text-blue-800">{entry.version}</div>
                <div className="text-xs text-gray-500">{entry.date}</div>
              </div>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                {entry.items.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">※ 最新の更新内容が一番上に表示されます。</p>
      </Section>
    </div>
  );
}
